/**
 * UI Block Renderer Component
 * 
 * This component is responsible for rendering structured JSON responses from the chat API
 * into appropriate UI components based on the block type. It maintains a component registry
 * that maps block types to their corresponding React components.
 * 
 * The renderer supports:
 * - Text blocks (rendered as markdown using the Response component)
 * - Confirmation blocks (binary choice dialogs)
 * - Select blocks (multiple choice selections)
 * - Editable list blocks (list management with add/remove/edit capabilities)
 * - Chart blocks (data visualization):
 *   - Line charts (trend visualization)
 *   - Bar charts (categorical comparisons)
 *   - Area charts (cumulative data)
 *   - Pie charts (proportional data)
 *   - Radial charts (circular data)
 *   - Scatter charts (correlation analysis)
 * 
 * When users interact with interactive components (confirmation, select, editable_list),
 * the renderer emits UI action events back to the parent component, which then sends them
 * to the chat API. Chart components are read-only and do not emit action events.
 */

import React from "react";
import type { 
  UIBlock, 
  RenderPayload, 
  UIActionEvent,
  ContainerBlock,
} from "@/types/ui-blocks";
import { isContainer, renderContainerBlock, dispatchBlock, getAutoBehaviours } from "./block-renderer-utils";
import { BehaviourSlot } from "./behaviour-slot";

export interface UIBlockRendererProps {
  /** The structured JSON payload containing blocks to render */
  payload: RenderPayload;
  /** Callback fired when user interacts with UI components */
  onUIAction: (event: UIActionEvent) => void;
  /** Optional className for the container */
  className?: string;
  /** When true, interactive elements are disabled */
  readOnly?: boolean;
  /** Chat history ID for behaviour action state management */
  chatHistoryId?: string;
  /** Behaviour action state map keyed by action ID */
  behaviourActionState?: Record<string, unknown>;
}

/**
 * Recursive function to render blocks, including nested containers
 */
const renderBlocksRecursive = (
  blocks: (UIBlock | ContainerBlock)[],
  onUIAction: (event: UIActionEvent) => void,
  readOnly?: boolean,
  chatHistoryId?: string,
  behaviourActionState?: Record<string, unknown>
): React.ReactNode[] => {
  return blocks.map((block, index) => {
    const autoBehaviours = getAutoBehaviours(block);

    if (isContainer(block)) {
      return (
        <React.Fragment key={`container-wrapper-${index}`}>
          {renderContainerBlock(
            block as ContainerBlock,
            (innerBlocks) => renderBlocksRecursive(innerBlocks, onUIAction, readOnly, chatHistoryId, behaviourActionState),
            { includeWrapper: true, index }
          )}
          {autoBehaviours.length > 0 && (
            <BehaviourSlot
              behaviours={autoBehaviours}
              onUIAction={onUIAction}
              readOnly={readOnly}
              chatHistoryId={chatHistoryId}
              behaviourActionState={behaviourActionState}
            />
          )}
        </React.Fragment>
      );
    }

    // Regular block rendering via unified dispatcher
    return (
      <div key={index}>
        {dispatchBlock({
          block: block as UIBlock,
          onUIAction,
          readOnly,
          chatHistoryId,
          behaviourActionState,
        })}
        {autoBehaviours.length > 0 && (
          <BehaviourSlot
            behaviours={autoBehaviours}
            onUIAction={onUIAction}
            readOnly={readOnly}
            chatHistoryId={chatHistoryId}
            behaviourActionState={behaviourActionState}
          />
        )}
      </div>
    );
  });
};


/**
 * Main UI Block Renderer Component
 * 
 * Takes a structured JSON payload and renders each block using the unified dispatcher.
 * Supports recursive container rendering with nested blocks.
 * Handles unknown block types gracefully by showing a fallback message.
 */
export const UIBlockRenderer: React.FC<UIBlockRendererProps> = ({
  payload,
  onUIAction,
  className,
  readOnly,
  chatHistoryId,
  behaviourActionState
}) => {
  // Filter out stacked_suggestions block as it's handled separately by the Chat component
  // Suggestions are typically the last block and are used to populate empty state UI
    const displayBlocks = payload.blocks.filter(block => block.type !== 'stacked_suggestions');

  return (
    <div className={className}>
      {/* Render optional title */}
      {payload.title && (
        <h3 className="text-[32px] font-semibold mb-4 leading-[40px] text-custom-text-primary">{payload.title}</h3>
      )}

      {/* Render each block recursively (excluding stacked_suggestions) */}
      <div className="space-y-4">
        {renderBlocksRecursive(displayBlocks, onUIAction, readOnly, chatHistoryId, behaviourActionState)}
      </div>
    </div>
  );
};
